import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthUtils } from './auth.utils';
import { AuthAppService } from '@modules/auth';
import { Observable, catchError, throwError, switchMap } from 'rxjs';

/**
 * Endpoints excluded from automatic 401 handling and token refresh
 * These paths should handle authentication errors manually
 */
const AUTH_EXCLUDED_ENDPOINTS = [
    '/api/auth/login',
    '/api/auth/logout',
    '/api/auth/register',
    '/api/auth/refresh-token',
    '/api/auth/forgot-password',
] as const;

/**
 * Attempt a token refresh and retry the original request on success.
 * Extracted at module level to keep nesting depth ≤ 4.
 */
function refreshAndRetry(
    originalError: HttpErrorResponse,
    request: HttpRequest<unknown>,
    next: HttpHandlerFn,
    authService: AuthAppService,
    onFailure: () => void
): Observable<HttpEvent<unknown>> {
    // Attempt token refresh (handles cookie-based token rotation).
    // The browser will automatically send the refresh_token cookie.
    return authService.refreshCurrentToken().pipe(
        switchMap((success) => {
            if (success) {
                // Retry the original request — new access_token cookie is now set
                return next(request);
            }
            // signOut() is a cold Observable — must be subscribed to clear state
            authService.signOut().subscribe();
            onFailure();
            return throwError(() => originalError);
        }),
        catchError(() => {
            // signOut() is a cold Observable — must be subscribed to clear state
            authService.signOut().subscribe();
            onFailure();
            return throwError(() => originalError);
        })
    );
}

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthAppService);
    const router = inject(Router);

    const redirectToSignIn = (): void => {
        const redirectURL = location.pathname + location.search;
        router.navigate(['/sign-in'], { queryParams: { redirectURL } });
    };

    const addAuthHeader = (request: HttpRequest<unknown>, token: string): HttpRequest<unknown> => {
        return request.clone({
            headers: request.headers.set('Authorization', `Bearer ${token}`)
        });
    };

    const isExcludedEndpoint = AUTH_EXCLUDED_ENDPOINTS.some(excludedPath =>
        req.url.toLowerCase().includes(excludedPath.toLowerCase())
    );

    const proceedWithRequest = (request: HttpRequest<unknown>): Observable<HttpEvent<unknown>> => {
        return next(request).pipe(
            catchError((error) => {
                // Catch "401 Unauthorized" responses
                if (!isExcludedEndpoint && error instanceof HttpErrorResponse && error.status === 401) {
                    return refreshAndRetry(error, request, next, authService, redirectToSignIn);
                }
                return throwError(() => error);
            })
        );
    };

    if (!req.url.includes('/api/') || isExcludedEndpoint) {
        return proceedWithRequest(req);
    }

    // Token is valid — add it and proceed
    if (!!authService.aToken && !AuthUtils.isTokenExpired(authService.aToken)) {
        return proceedWithRequest(addAuthHeader(req, authService.aToken));
    }

    // Token is expired but refresh token exists — refresh first then proceed
    if (!!authService.aToken && !!authService.rToken) {
        return authService.refreshCurrentToken().pipe(
            switchMap((success) => {
                if (success) {
                    // Refresh successful — add token header if available (token-based auth),
                    // otherwise proceed as-is (cookie-based auth relies on Set-Cookie).
                    const newReq = authService.aToken ? addAuthHeader(req, authService.aToken) : req;
                    return proceedWithRequest(newReq);
                }
                // signOut() is a cold Observable — must be subscribed to clear state
                authService.signOut().subscribe();
                redirectToSignIn();
                return throwError(() => new Error('Token refresh failed'));
            }),
            catchError((err) => {
                // signOut() is a cold Observable — must be subscribed to clear state
                authService.signOut().subscribe();
                redirectToSignIn();
                return throwError(() => err);
            })
        );
    }

    // No valid tokens — proceed without auth header (for public endpoints)
    return proceedWithRequest(req);
};


